import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { Plus, X } from "lucide-react"
import { useGmailIntegrationsQuery } from "modules/settings/hooks/gmail/useGmailIntegrationsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { GmailIntegrationsResponseDataSchema } from "schema"
import { z } from "zod"

type GmailOption = React.ComponentProps<typeof Combobox>["options"][number] & {
  data?: z.infer<typeof GmailIntegrationsResponseDataSchema>[number]
}
type Option = GmailOption | undefined

type Props = {
  onChange?: (value: Option) => void
  onChangeMany?: (value: GmailOption[]) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  avoidDisplaySelected?: boolean
  hasCreateNew?: boolean
  value?: string | null
  values?: string[]
  isDisabled?: boolean
}

export const GmailAccountFormSelector = ({
  onChange,
  onChangeMany,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  avoidDisplaySelected,
  hasCreateNew,
  value,
  values = [],
  isDisabled,
}: Props) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({ value: searchQuery })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data, isPending } = useGmailIntegrationsQuery()

  const allOptions = useMemo(() => {
    if (!data) return []

    return data.map((account) => ({
      label: account.email,
      value: account.id,
      data: account,
    }))
  }, [data])

  const selectedOptions = useMemo(() => {
    return values.flatMap((selectedValue) => {
      const option = allOptions.find((e) => e.value === selectedValue)
      return option ? [option] : []
    })
  }, [allOptions, values])

  const options = useMemo(() => {
    const selectedValueSet = new Set(values)

    return allOptions
      .filter((e) =>
        e.data?.email.match(
          new RegExp(debouncedSearchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
        ),
      )
      .filter((e) => !onChangeMany || !selectedValueSet.has(e.value))
  }, [allOptions, debouncedSearchQuery, onChangeMany, values])

  function onComboboxChange(option: Option) {
    if (onChangeMany) {
      if (!option) return
      onChangeMany([...selectedOptions, option])
      setSearchQuery("")
      return
    }

    if (!avoidDisplaySelected) {
      setSelectedValue(option)
    }
    onChange?.(option)
  }

  function onRemove(valueToRemove: string) {
    if (!onChangeMany) return

    onChangeMany(selectedOptions.filter((option) => option.value !== valueToRemove))
  }

  useEffect(() => {
    if (value && data) {
      const result = allOptions.find((e) => e.value === value)

      if (result) {
        setSelectedValue(result)
      }
    }
  }, [value, data, allOptions])

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {onChangeMany && selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm"
              onClick={() => onRemove(option.value)}
              disabled={isDisabled}
            >
              <span>{option.label}</span>
              <X className="size-3" />
            </button>
          ))}
        </div>
      )}
      <Combobox
        options={options}
        value={onChangeMany ? undefined : selectedValue}
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending || isDisabled}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className="w-full"
        actions={
          hasCreateNew
            ? [
                {
                  icon: Plus,
                  text: t("campaigns.trigger.metadata.emailInbox.addAccount"),
                  to: "/settings/channels/gmail",
                  target: "_blank",
                },
              ]
            : undefined
        }
      />
    </div>
  )
}
